import { NodeStatus, ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { Table } from '@dbt-labs/sourdough';

import { FreshnessStatus, SourceAppliedStateNestedNode } from '@/__generated__/graphql';
import { ResourceChip } from '@/components/ResourceChip';
import { LinkCell } from '@/components/Table/LinkCell';
import { NodeStatusCell } from '@/components/Table/NodeStatusCell';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';

export type ResourceWithStatus = {
  name: string;
  uniqueId: string;
  resourceType: ResourceTypeExplorer;
  status: NodeStatus | undefined | null;
};

type ResourceTestStatusTableProps = {
  resources: ResourceWithStatus[];
  className?: string;
};

const freshnessStatusToNodeStatus: Record<FreshnessStatus, NodeStatus> = {
  [FreshnessStatus.Pass]: 'pass',
  [FreshnessStatus.Warn]: 'warn',
  [FreshnessStatus.Error]: 'error',
  [FreshnessStatus.Unknown]: 'unknown',
  [FreshnessStatus.Skipped]: 'skipped',
  [FreshnessStatus.Outdated]: 'outdated',
  [FreshnessStatus.Unconfigured]: 'unconfigured',
};

export const fromSource = (
  source: SourceAppliedStateNestedNode,
): ResourceWithStatus => {
  const name = source.uniqueId.split('.').slice(2).join('.');
  return {
    name,
    status: source.freshness?.freshnessStatus
      ? freshnessStatusToNodeStatus[source.freshness.freshnessStatus]
      : null,
    resourceType: 'source',
    uniqueId: source.uniqueId,
  };
};

const HEADER_HEIGHT = 43;
const ROW_HEIGHT = 64;
const MAX_TABLE_HEIGHT = 5.5 * ROW_HEIGHT + HEADER_HEIGHT;

export const ResourceStatusTable = ({
  resources,
  className,
}: ResourceTestStatusTableProps) => {
  const resourceLink = useLinkToExplorer('resourceDetails');
  const { accountId, projectId, environmentType } = useUrlProjectParams();

  const tableHeight = Math.min(
    HEADER_HEIGHT + resources.length * ROW_HEIGHT,
    MAX_TABLE_HEIGHT,
  );

  return (
    <Table
      data={resources}
      className={className}
      isLoading={false}
      height={tableHeight}
      rowHeight={ROW_HEIGHT}
      onRowClickLinkGenerator={(resource) => {
        return resourceLink({
          accountId,
          dbtUniqueId: resource.uniqueId,
          environmentType,
          projectId,
        });
      }}
      isSortable={false}
      columns={[
        {
          header: 'Resource type',
          accessorKey: 'resourceType',
          minSize: 120,
          maxSize: 140,
          cell: ({ getValue }) => (
            <span className="space-x-1 whitespace-nowrap">
              <ResourceChip resourceType={getValue() as any} />
            </span>
          ),
        },
        {
          header: 'Name',
          accessorKey: 'name',
          minSize: 60,
          cell: LinkCell,
        },
        {
          header: 'Status',
          accessorKey: 'status',
          cell: NodeStatusCell,
          maxSize: 120,
        },
      ]}
    />
  );
};
